import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { BenefitDesignStep } from 'adminDashboard/benefitDetails/design/perks/DesignStepsMapping'
//
import global from 'common'

import {
  DefinePerksBenefitRedesignDecisionRequest,
  canInitiateRenewal,
  definePerksBenefitRedesignDecision,
  getRenewalPlanPeriod,
} from 'core'
import benefitPlanPeriods, { PlanPeriodDto } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import getBenefitProductClasses, {
  BenefitProductClassResponse,
} from 'core/application/adminDashboard/benefitDetails/getBenefitProductClasses'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'

import { BenefitPlanPeriodDropDown, Card, Col, Row, Skeleton } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'

import { useCommandRequestV2, useQueryRequestV2 } from '../../../../../common/hooks'
import PerksProductClassDetails from '../../../common/productClassDetails/perksProductClassDetails'
import { DESIGN_BENEFIT_PATH } from '../../../design'
import SummaryEmpty from '../../SummaryEmpty'
import SummaryTitle from '../../SummaryTitle'
import RenewalDesignInfoBox from '../../common/RenewalDesignInfoBox'
import RenewalBanner from './RenewalBanner'

const PerksDesignBenefitSummary: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const match = useRouteMatch()
  const { data: planPeriods } = useQueryRequestV2<PlanPeriodDto[]>({
    initialState: null,
    queryFn: () => benefitPlanPeriods(),
  })

  const [activePlanPeriod, setActivePlanPeriod] = useState<PlanPeriodDto | undefined>(undefined)
  const {
    data: productClasses,
    isLoading: areProductClassesLoading,
    sendQuery: sendGetPlanPeriodDetails,
  } = useQueryRequestV2<BenefitProductClassResponse[]>({
    initialState: null,
    queryFn: () => getBenefitProductClasses(activePlanPeriod?.id),
    enable: !!activePlanPeriod,
  })

  const { data: showRenewalBanner } = useQueryRequestV2({ initialState: false, queryFn: () => canInitiateRenewal() })

  const { data: renewalPlanPeriod } = useQueryRequestV2({ initialState: null, queryFn: () => getRenewalPlanPeriod() })

  const benefitDesignFullPath = `${match.path}/${DESIGN_BENEFIT_PATH}/`

  const { sendCommand: sendSetPerkBenefitRedesignDecisionCommand } = useCommandRequestV2(
    (request: DefinePerksBenefitRedesignDecisionRequest) => definePerksBenefitRedesignDecision(request),
  )

  useEffect(() => {
    planPeriods?.length > 0 && setActivePlanPeriod(planPeriods.find((x) => x.active))
  }, [planPeriods])

  const validStatuses = [BenefitDesignStatus.Completed, BenefitDesignStatus.Signed]
  useEffect(
    () =>
      void (
        activePlanPeriod &&
        (async () => {
          await sendGetPlanPeriodDetails(() => getBenefitProductClasses(activePlanPeriod.id))
        })()
      ),
    [activePlanPeriod],
  )

  const onPlanPeriodChanged = (planPeriodId: string) =>
    setActivePlanPeriod(planPeriods.find((x) => x.id === planPeriodId))

  const onBenefitStartDataClick = () =>
    history.push(`${benefitDesignFullPath}${activePlanPeriod.id}/${BenefitDesignStep.BenefitStartDate}`)
  return (
    <Card
      title={
        <Row justify={'space-between'} className={'w-100'}>
          <Col>
            <SummaryTitle />
          </Col>
          <Col>
            {activePlanPeriod && (
              <BenefitPlanPeriodDropDown
                text={'Allowance Period'}
                defaultValue={activePlanPeriod?.id}
                planPeriods={planPeriods}
                onPlanPeriodSelected={onPlanPeriodChanged}
                showStartDateAndEndDate={true}
              />
            )}
          </Col>
        </Row>
      }
      className="benefit_details__summary"
    >
      {showRenewalBanner && <RenewalBanner />}
      <Skeleton active loading={!activePlanPeriod} paragraph={{ rows: 10 }}>
        {activePlanPeriod && (
          <Card title={<SummaryTitle />} className="benefit_details__summary">
            {activePlanPeriod.benefitDesignStatus === BenefitDesignStatus.Completed ||
            activePlanPeriod.benefitDesignStatus === BenefitDesignStatus.Signed ? (
              <>
                <Row>
                  {renewalPlanPeriod && !validStatuses.includes(renewalPlanPeriod.status) && (
                    <RenewalDesignInfoBox
                      renewalPlanPeriod={renewalPlanPeriod}
                      renewalDecisionHandler={(useSame) => sendSetPerkBenefitRedesignDecisionCommand({ useSame })}
                    />
                  )}
                  <Col span={24}>
                    <Row className="with_card">
                      <Col span={18} className="content">
                        <h5 className="mb-0">{t('admin-dashboard.benefit-details.summary.start-date-title')}</h5>
                        <h5 className="mb-0">{parseZoneFormat(activePlanPeriod.startDate)}</h5>
                      </Col>

                      <Col span={6} className="flex justify-end">
                        <global.components.general.Button
                          type="link"
                          disabled={activePlanPeriod.benefitDesignStatus === BenefitDesignStatus.Signed}
                          onClick={onBenefitStartDataClick}
                        >
                          {t('admin-dashboard.benefit-details.summary.edit-button')}
                        </global.components.general.Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <BenefitSummary
                  planPeriodId={activePlanPeriod.id}
                  productClasses={productClasses}
                  areProductClassesLoading={areProductClassesLoading}
                />
              </>
            ) : (
              <SummaryEmpty planPeriodId={activePlanPeriod.id} />
            )}
          </Card>
        )}
      </Skeleton>
    </Card>
  )
}

export default PerksDesignBenefitSummary

type BenefitSummaryProps = {
  planPeriodId: string
  productClasses: BenefitProductClassResponse[]
  areProductClassesLoading: boolean
}

const BenefitSummary = ({ planPeriodId, productClasses, areProductClassesLoading }: BenefitSummaryProps) => {
  return (
    <Row className={'mb-8 mt-4'}>
      {productClasses && (
        <Skeleton loading={areProductClassesLoading}>
          {productClasses.map((item, index) => (
            <Col key={item.id} span={24}>
              <PerksProductClassDetails collapseKey={index} item={item} colSpan={24} planPeriodId={planPeriodId} />
            </Col>
          ))}
        </Skeleton>
      )}
    </Row>
  )
}
